import type {
  ObservabilityFidelity,
  ObservabilityProvenance,
  ObservabilitySource,
  Phase,
} from './index';

export const MANAGEMENT_TIMELINE_VIEW_VALUES = [
  'management',
  'technical',
] as const;
export const MANAGEMENT_TIMELINE_DEFAULT_VIEW = 'management' as const;

export const MANAGEMENT_TIMELINE_MARKER_TYPE_VALUES = [
  'activity',
  'session',
  'workflow',
  'skill',
  'task',
  'worktree',
  'artifact',
  'approval',
  'vcs',
  'risk',
  'observability_gap',
] as const;

export const MANAGEMENT_TIMELINE_OUTCOME_VALUES = [
  'started',
  'completed',
  'failed',
  'integrated',
  'published',
  'approved',
  'rejected',
  'pending',
  'blocked',
  'warning',
  'unknown',
] as const;

export const MANAGEMENT_TIMELINE_GROUP_BY_VALUES = [
  'type',
  'participant',
  'skill',
  'task',
  'outcome',
  'phase',
] as const;

export const MANAGEMENT_TIMELINE_DEFAULT_PAGE = 1;
export const MANAGEMENT_TIMELINE_DEFAULT_LIMIT = 20;
export const MANAGEMENT_TIMELINE_MAX_LIMIT = 100;

export type ManagementTimelineView =
  (typeof MANAGEMENT_TIMELINE_VIEW_VALUES)[number];
export type ManagementTimelineGroupBy =
  (typeof MANAGEMENT_TIMELINE_GROUP_BY_VALUES)[number];

export type ManagementTimelineMarkerType =
  (typeof MANAGEMENT_TIMELINE_MARKER_TYPE_VALUES)[number];
export type ManagementTimelineOutcome =
  (typeof MANAGEMENT_TIMELINE_OUTCOME_VALUES)[number];

export interface ManagementTimelineTechnicalEvent {
  id: string;
  eventType: string;
  timestamp: string;
  summary: string;
  metadata: Record<string, unknown>;
}

export interface ManagementTimelineMarker {
  id: string;
  activityId: string;
  type: ManagementTimelineMarkerType;
  title: string;
  summary: string;
  timestamp: string;
  outcome: ManagementTimelineOutcome;
  participantId: string | null;
  skillName: string | null;
  taskNumber: string | null;
  phase: Phase | null;
  source: ObservabilitySource | 'system';
  provenance: ObservabilityProvenance | 'derived';
  fidelity: ObservabilityFidelity | 'derived';
  groupKey: string;
  technicalEvents: ManagementTimelineTechnicalEvent[];
  attentionRequired?: boolean;
  evidenceIds?: string[];
}

export interface ManagementTimelineGroupMetadata {
  type: ManagementTimelineMarkerType;
  outcome: ManagementTimelineOutcome;
  skillName: string | null;
  title: string;
  attentionRequired: boolean;
  evidenceIds: string[];
}

export interface ManagementTimelineGroup {
  key: string;
  label: string;
  count: number;
  firstTimestamp?: string;
  lastTimestamp?: string;
  metadata?: ManagementTimelineGroupMetadata;
}

export interface ManagementTimelineFilters {
  view?: ManagementTimelineView;
  participant?: string;
  skill?: string;
  task?: string;
  outcome?: ManagementTimelineOutcome;
  type?: ManagementTimelineMarkerType;
  from?: string;
  to?: string;
  attention?: boolean;
  groupBy?: ManagementTimelineGroupBy;
  groupKey?: string;
}

export interface ManagementTimelineQuery extends ManagementTimelineFilters {
  page?: number;
  limit?: number;
}

export interface ManagementTimelinePage {
  activityId: string;
  generatedAt: string;
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
  filters: ManagementTimelineFilters;
  groups: ManagementTimelineGroup[];
  markers: ManagementTimelineMarker[];
}
